<script setup>
import { ref, computed, onMounted, onUnmounted, defineProps, defineEmits, watch } from 'vue';
import XetaSenseConfig from '@/components/modals/XetaSenseConfig.vue';

const props = defineProps({
  configDetails: {
    type: Object,
    required: true
  },
  updateSuccess: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['changedDetails']);

// Refs for template
const selectedPort = ref(null);
const hoveredPort = ref(null);
const xetaImage = ref(null);
const svgOverlay = ref(null);
const canWire = ref({high:"", low:""});
const baseScale = ref(1);

// Original port positions (unscaled)
const originalPorts = ref([
  { id: 'CI 4', x: 150, y: 600, highlightX: 370, highlightY: 900, wireX: 370, wireY: 1175, channel: '12', wired: false, enabled: false },
  { id: 'CI 3', x: 150, y: 450, highlightX: 460, highlightY: 900, wireX: 460, wireY: 1175, channel: '11', wired: false, enabled: false },
  { id: 'CI 2', x: 150, y: 300, highlightX: 552, highlightY: 900, wireX: 552, wireY: 1175, channel: '10', wired: false, enabled: false },
  { id: 'CI 1', x: 150, y: 150, highlightX: 642, highlightY: 900, wireX: 642, wireY: 1175, channel: '9', wired: false, enabled: false },
  { id: 'VI 8', x: 1360, y: 1200, highlightX: 732, highlightY: 900, wireX: 732, wireY: 1175, channel: '8', wired: false, enabled: false },
  { id: 'VI 7', x: 1360, y: 1050, highlightX: 780, highlightY: 900, wireX: 780, wireY: 1175, channel: '7', wired: false, enabled: false },
  { id: 'VI 6', x: 1360, y: 900, highlightX: 826, highlightY: 900, wireX: 826, wireY: 1175, channel: '6', wired: false, enabled: false },
  { id: 'VI 5', x: 1360, y: 750, highlightX: 870, highlightY: 900, wireX: 870, wireY: 1175, channel: '5', wired: false, enabled: false },
  { id: 'VI 4', x: 1360, y: 600, highlightX: 916, highlightY: 900, wireX: 916, wireY: 1175, channel: '4', wired: false, enabled: false },
  { id: 'VI 3', x: 1360, y: 450, highlightX: 960, highlightY: 900, wireX: 960, wireY: 1175, channel: '3', wired: false, enabled: false },
  { id: 'VI 2', x: 1360, y: 300, highlightX: 1004, highlightY: 900, wireX: 1004, wireY: 1175, channel: '2', wired: false, enabled: false },
  { id: 'VI 1', x: 1360, y: 150, highlightX: 1048, highlightY: 900, wireX: 1048, wireY: 1175, channel: '1', wired: false, enabled: false }
]);

// Computed scaled ports with responsive dimensions
const scaledPorts = computed(() => {
  return originalPorts.value.map(port => {
    const scale = baseScale.value;
    const baseRadius = Math.max(10, 20 * scale);
    
    return {
      ...port,
      x: port.x,
      y: port.y,
      highlightX: port.highlightX,
      highlightY: port.highlightY,
      wireX: port.wireX,
      wireY: port.wireY,
      radius: baseRadius,
      touchRadius: baseRadius * 1.25,
      strokeWidth: Math.max(1, 2 * scale),
      wireSize: Math.max(40, 80 * scale),
      rectWidth: Math.max(10, 20 * scale),
      rectHeight: Math.max(30, 60 * scale),
      labelOffset: baseRadius * 2,
      fontSize: Math.max(12, 14 * scale)
    };
  });
});

// Check if certain ports are already configured
const setupPorts = () => {
  const channels = props.configDetails.channels;
  originalPorts.value.forEach(port => {
    // Reset states first
    port.wired = false;
    port.enabled = false;
    
    const channelConfig = channels[port.channel];
    if (channelConfig.scale.min !== 0 || channelConfig.scale.max !== 0 || channelConfig.scale.offset !== 0) {
      port.wired = true;
    }
    if (channelConfig.enabled) {
      port.enabled = true;
    }
  });
};

// Methods
const handlePortClick = (channel) => {
  selectedPort.value = props.configDetails.channels[channel];
};

const handleConfigChange = (newDetails) => {
  const newConfig = props.configDetails;
  newConfig.channels[newDetails.channel] = newDetails;
  emit('changedDetails', newConfig);
};

const closeModal = () => {
  selectedPort.value = null;
};

const getConnectionPath = (port) => {
  const startX = port.x;
  const startY = port.y;
  const endX = port.x <= 400 ? port.highlightX : port.highlightX + 40;
  const endY = port.highlightY;
  
  return `M ${startX} ${startY} L ${endX} ${endY}`;
};

// Handle image load and scaling
const handleImageLoad = () => {
  const img = xetaImage.value;
  if (img) {
    // Calculate scale based on image width
    baseScale.value = img.clientWidth / 1520;
  }
};

// Watch for window resize
let resizeTimeout;
const handleResize = () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(handleImageLoad, 100);
};

onMounted(() => {
  // Set the proper can wire colors
  if (props.configDetails.bus_id === "can0") {
    canWire.value.high = require("@/assets/images/wire_yellow.png");
    canWire.value.low = require("@/assets/images/wire_green.png");
  } else if (props.configDetails.bus_id === "can1") {
    canWire.value.high = require("@/assets/images/wire_white.png");
    canWire.value.low = require("@/assets/images/wire_blue.png");
  } else if (props.configDetails.bus_id === "can2") {
    canWire.value.high = require("@/assets/images/wire_orange.png");
    canWire.value.low = require("@/assets/images/wire_violet.png");
  }


  window.addEventListener('resize', handleResize);
  // Initial load if image is already cached
  if (xetaImage.value?.complete) {
    handleImageLoad();
  }
  setupPorts();
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  clearTimeout(resizeTimeout);
});

watch(() => props.configDetails, () => {
  setupPorts();
}, { deep: true });

// Watch for successful update to close modal
watch(() => props.updateSuccess, (newValue) => {
  if (newValue) {
    closeModal();
  }
});
</script> 

<template>
  <div class="relative inline-block">
    <!-- Container that holds both image and SVG -->
    <div class="relative">
      <img 
        src="@/assets/images/XetaSence.png" 
        alt="XetaSense&#8482;"
        class="w-full h-auto"
        ref="xetaImage"
        @load="handleImageLoad"
      >
      
      <!-- SVG overlay with the same dimensions as the image -->
      <svg
        class="absolute top-0 left-0 w-full h-full"
        :viewBox="`0 0 1520 1300`"
        preserveAspectRatio="xMidYMid meet"
        ref="svgOverlay"
      >

        <!-- CAN High wire -->
        <image
          :href="canWire.high"
          :x="-1050" 
          :y="-135"
          width="250"
          height="250"
          class="rotate-180"
        />

        <!-- CAN Low wire -->
        <image
          :href="canWire.low"
          :x="-1095" 
          :y="-135"
          width="250"
          height="250"
          class="rotate-180"
        />

        <!-- Left side separator line -->
        <text
          x="110"
          y="-30"
          font-size="50"
          text-anchor="middle"
          class="fill-gray-700 font-medium select-none pointer-events-none"
        >
          Current
        </text>
        <text
          x="110"
          y="30"
          font-size="50"
          text-anchor="middle"
          class="fill-gray-700 font-medium select-none pointer-events-none"
        >
          Inputs
        </text>
        
        <!-- Right side separator line -->
        <text
          x="1400"
          y="-30"
          font-size="50"
          text-anchor="middle"
          class="fill-gray-700 font-medium select-none pointer-events-none"
        >
          Voltage
        </text>
        <text
          x="1400"
          y="30"
          font-size="50"
          text-anchor="middle"
          class="fill-gray-700 font-medium select-none pointer-events-none"
        >
          Inputs
        </text>

        <!-- Connection paths and highlight rectangles that appear on hover -->
        <g v-for="(port, index) in scaledPorts" :key="'path-'+index">

          <!-- Red wire image -->
          <image
            href="@/assets/images/wire_red.png"
            :x="port.wireX - 108" 
            :y="port.wireY - 70"
            width="250"
            height="250"
            :class="{
              'transition-all duration-300 ease-out transform origin-center': true,
              'opacity-100 translate-y-0': hoveredPort === port.id || port.wired,
              'opacity-0 translate-y-10': hoveredPort !== port.id && !port.wired
            }"
          />
          
          <!-- Highlight rectangle -->
          <rect
            :x="port.highlightX"
            :y="port.highlightY"
            width="40"
            height="100"
            :class="{
              'transition-all': true,
              'stroke-black fill-green-100/30': hoveredPort === port.id,
              'stroke-transparent fill-transparent': hoveredPort !== port.id
            }"
            stroke-width="1"
            rx="4"
          />
          
          <!-- Connection path from circle to label rectangle -->
          <path
            :d="getConnectionPath(port)"
            :class="{
              'transition-all': true,
              'stroke-black opacity-70': hoveredPort === port.id,
              'stroke-transparent opacity-0': hoveredPort !== port.id
            }"
            fill="none"
            stroke-width="5"
            stroke-dasharray="4,4"
          />
        </g>

        <!-- Port circles -->
        <g v-for="(port, index) in scaledPorts" :key="'port-'+index">
          <!-- Larger invisible circle for easier clicking -->
          <circle
            :cx="port.x"
            :cy="port.y"
            r="50"
            fill="transparent"
            @click="handlePortClick(port.channel)"
            class="cursor-pointer"
          />
          
          <!-- Visible port indicator -->
          <circle
            :cx="port.x"
            :cy="port.y"
            r="50"
            :class="{
              'cursor-pointer transition-all hover:animate-none': true,
              'fill-green-500/50 stroke-green-600': port.wired && port.enabled,
              'fill-red-500/50 stroke-red-600': port.wired && !port.enabled,
              'fill-transparent stroke-gray-400': !port.wired,
              'hover:fill-blue-300/70': hoveredPort === port.id
            }"
            stroke-width="5"
            @mouseenter="hoveredPort = port.id"
            @mouseleave="hoveredPort = null"
            @click="handlePortClick(port.channel)"
          />
          
          <!-- Port label -->
          <text
            :x="port.x <= 400 ? port.x - 95 : port.x + 95"
            :y="port.y + 15"
            font-size="40"
            text-anchor="middle"
            class="fill-gray-700 font-medium select-none pointer-events-none"
          >
            {{ port.id }}
          </text>
        </g>
      </svg>
    </div>

    <!-- Modal -->
    <XetaSenseConfig
      v-if="selectedPort"
      :configDetails="selectedPort"
      @changedDetails="handleConfigChange"
      @close="closeModal"
    />
  </div>
</template>